import {
  Box,
  CircularProgress,
  Divider,
  Icon,
  InputAdornment,
  Modal,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useState } from 'react'
import AdxInfoModal from 'components/reusable/modal/AdxInfoModal'
import AdxCheckbox from 'components/custom/AdxCheckbox'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { getCourseAvailable, postCourse } from 'services/api/modules/upskill'
import AdxInput from 'components/custom/AdxInput'
import SearchIcon from '@mui/icons-material/Search'
import AdxButton from 'components/custom/AdxButton'
import AdxEmptyStateV2 from 'components/reusable/emptyState/AdxEmptyStateV2'

const CourseListModal = ({ isModalOpen, handleClose }) => {
  const { componentHeight } = useTheme()
  const isLarge = useMediaQuery((theme) => theme.breakpoints.up('lg'))
  const [confirmModalState, setConfirmModalState] = useState(false)
  const [successModalState, setSuccessModalState] = useState(false)
  const [rejectModalState, setRejectModalState] = useState(false)
  const [courseId, setCourseId] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredData, setFilteredData] = useState([])

  const courseIsSelected = courseId.length > 0

  const handleCourseId = (e, id) => {
    const { checked } = e.target
    if (checked) {
      setCourseId([...courseId, id])
    } else if (!checked) {
      setCourseId(courseId.filter((item) => item !== id))
    }
  }

  const queryClient = useQueryClient()
  const {
    isLoading: listIsLoading,
    error: listError,
    data: listData,
  } = useQuery(
    ['getListData'],
    async () => {
      const { data } = await getCourseAvailable()
      return data
    },
    {
      refetchOnWindowFocus: false,
      onSuccess: (data) => setFilteredData(data),
    }
  )
  const { mutate: mutateCourse, isLoading: postCourseIsLoading } = useMutation(
    (value) => postCourse(value),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries('courseData')
        await queryClient.invalidateQueries('getListData')
        handleClose()
        setConfirmModalState(false)
        setSuccessModalState(true)
      },
      onError: (error) => {
        console.error(error)
        handleClose()
        setRejectModalState(true)
      },
    }
  )

  const handleSelect = () => {
    mutateCourse({ nex_edu_course_intake_id: courseId })
  }

  const handleSearchChange = (event) => {
    const newSearchTerm = event.target.value
    setSearchTerm(newSearchTerm)
    filterData(newSearchTerm)
  }

  const filterData = (term) => {
    const filtered = listData.filter((item) =>
      item.course_name.toLowerCase().includes(term.toLowerCase())
    )
    setFilteredData(filtered)
  }

  return (
    <>
      <Modal
        open={isModalOpen}
        onClose={handleClose}
        handleClose={handleClose}
        sx={{
          height: '100vh',
          marginInline: 'auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Box
          bgcolor="white.main"
          width={isLarge ? '650px' : '90vw'}
          borderRadius="4.5px"
          overflow="clip"
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '15px 24px',
            }}
          >
            <Typography>List of available courses</Typography>
            <Icon onClick={handleClose} sx={{ color: 'dark-text.main', cursor: 'pointer' }}>
              close_rounded
            </Icon>
          </Box>
          <Divider />
          <Box sx={{ padding: '24px' }}>
            <Box
              sx={{
                bgcolor: 'gray-background.main',
                padding: '16px',
                borderRadius: '4.5px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}
            >
              <AdxInput
                value={searchTerm}
                onChange={handleSearchChange}
                size="standard"
                placeholder="Search course name"
                sx={{
                  backgroundColor: 'white.main',
                }}
                sxInput={{}}
                startAdornment={
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                }
              />
              <Typography>Select a course</Typography>
              <Box
                className="scrollbar"
                sx={{
                  height: '40vh',
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                }}
              >
                {!listIsLoading && !listError && filteredData.length > 0 ? (
                  filteredData.map((item, index) => (
                    <Box
                      key={index}
                      borderRadius={'4.5px'}
                      display="flex"
                      alignItems="center"
                      padding={'8px'}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: 'white.main',
                        '&:hover': {
                          backgroundColor: 'alternate.light',
                        },
                      }}
                    >
                      <AdxCheckbox
                        onChange={(e) => {
                          handleCourseId(e, item.id)
                        }}
                      />
                      <Typography variant="subtitle1">{item.course_name}</Typography>
                    </Box>
                  ))
                ) : (
                  <AdxEmptyStateV2
                    icon="error"
                    message="No available courses found"
                    height={'100%'}
                  />
                )}
              </Box>
            </Box>
          </Box>
          <Box
            display="flex"
            flexDirection={isLarge ? 'row' : 'column'}
            gap={'10px'}
            bgcolor="alternate.main"
            padding="24px 24px 32px 24px"
            justifyContent="flex-end"
          >
            <AdxButton
              variant={'outlined'}
              sx={{
                width: isLarge ? '150px' : '100%',
                color: 'white.main',
                borderColor: 'white.main',
                '&:hover': {
                  backgroundColor: 'alternate.main',
                  color: 'white.main',
                  borderColor: 'white.main',
                },
              }}
              onClick={handleClose}
            >
              Cancel
            </AdxButton>
            <AdxButton
              variant={'contained'}
              onClick={courseIsSelected ? () => setConfirmModalState(true) : () => {}}
              disabled={!courseIsSelected}
              sx={{
                height: componentHeight.standard,
                width: isLarge ? '150px' : '100%',
                backgroundColor: 'white.main',
                color: 'alternate.main',
                borderColor: 'white.main',
                '&:hover': {
                  backgroundColor: 'white.main',
                  color: 'alternate.main',
                  borderColor: 'white.main',
                },
              }}
            >
              Select
            </AdxButton>
          </Box>
        </Box>
      </Modal>

      {/* Confirmation modal */}
      <Modal
        open={confirmModalState}
        onClose={() => setConfirmModalState(false)}
        handleClose={() => setConfirmModalState(false)}
        sx={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Box
          sx={{
            width: isLarge ? '358px' : '90vw',
            padding: '24px',
            bgcolor: 'white.main',
            borderRadius: '4.5px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              width: '48px',
              height: '48px',
              borderRadius: '100%',
              bgcolor: 'gray-background.main',
              mb: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon color="error" sx={{ fontSize: '28px' }}>
              warning
            </Icon>
          </Box>
          <Typography sx={{ textAlign: 'center', mb: '24px' }} fontWeight="bold">
            Are you sure you want to submit your course request(s)?
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: isLarge ? 'row' : 'column',
              gap: '10px',
              width: '100%',
            }}
          >
            <AdxButton variant={'outlined'} fullWidth onClick={() => setConfirmModalState(false)}>
              Close
            </AdxButton>
            <AdxButton variant={'contained'} fullWidth onClick={handleSelect}>
              {postCourseIsLoading ? <CircularProgress color="alternate" size={20} /> : 'Confirm'}
            </AdxButton>
          </Box>
        </Box>
      </Modal>

      {/* Successful modal */}
      <Modal
        open={successModalState}
        onClose={() => setSuccessModalState(false)}
        handleClose={() => setSuccessModalState(false)}
        sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Box
          sx={{
            width: '441px',
            bgcolor: 'white.main',
            borderRadius: '4.5px',
            padding: '32px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Icon sx={{ fontSize: '48px', color: 'alternate.main', mb: '16px' }}>check_circle</Icon>
          <Typography variant="h4" fontWeight="bold" sx={{ mb: '7px' }}>
            Course request submitted
          </Typography>
          <Typography
            fontWeight="bold"
            sx={{ color: 'light-gray-text.main', textAlign: 'center', mb: '68px' }}
          >
            Your course request has been successfully submitted.
          </Typography>
          <AdxButton variant={'contained'} fullWidth onClick={() => setSuccessModalState(false)}>
            Close
          </AdxButton>
        </Box>
      </Modal>

      {/* Error modal */}
      <AdxInfoModal
        variant={'error'}
        title={'Error has occured'}
        subtitle={'Please try again later'}
        open={rejectModalState}
        onClose={() => setRejectModalState(false)}
        handleClose={() => setRejectModalState(false)}
        muiIcon="info"
        iconColor="error.main"
        footerButtons
        firstBtnLabel={'Return to dashboard'}
        firstBtnHandle={() => {
          setRejectModalState(false)
          handleClose()
        }}
      >
        {/* <AdxButton
          variant={'contained'}
          color="alternate"
          fullWidth
          onClick={() => {
            setRejectModalState(false)
            handleClose()
          }}
          sx={{
            '&:hover': {
              opacity: 0.75,
            },
          }}
          //   isLoading={mutation.isLoading}
          //   disabled={mutation.isLoading}
        >
          Return to dashboard
        </AdxButton> */}
      </AdxInfoModal>
    </>
  )
}

export default CourseListModal
